import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { FileText, Loader2, Search, PlayCircle, CheckCircle2, Phone, User, Clock, Trash2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABELS = {
  pending: { ar: "قيد الانتظار", color: "bg-amber-100 text-amber-800 border-amber-200" },
  in_progress: { ar: "قيد التجهيز", color: "bg-blue-100 text-blue-800 border-blue-200" },
  completed: { ar: "مكتمل", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
};

const MedicalReportRequests = () => {
  const { token, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/medical-reports`, { headers });
      setRequests(res.data || []);
    } catch {
      toast.error("فشل في تحميل الطلبات");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [headers]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return requests.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (!s) return true;
      return [
        r.request_number,
        r.patient_full_name,
        r.medical_file_number,
        r.treating_doctor_name,
        r.requester_name,
        r.clinic_name,
      ].some((v) => (v || "").toLowerCase().includes(s));
    });
  }, [requests, statusFilter, search]);

  const stats = useMemo(() => ({
    pending: requests.filter((r) => r.status === "pending").length,
    in_progress: requests.filter((r) => r.status === "in_progress").length,
    completed: requests.filter((r) => r.status === "completed").length,
  }), [requests]);

  const changeStatus = async (request_id, new_status, note = "") => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/medical-reports/${request_id}/status`,
        { status: new_status, notes: note },
        { headers }
      );
      toast.success(new_status === "completed" ? "تم الاكتمال وإشعار مقدّم الطلب" : "تم تحديث الحالة");
      setDetail(res.data);
      load();
    } catch {
      toast.error("فشل في تحديث الحالة");
    } finally {
      setSaving(false);
    }
  };

  const openDetail = (r) => {
    setDetail(r);
    setNotes(r.notes || "");
  };

  const deleteRequest = async (request_id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا الطلب؟ لا يمكن التراجع.")) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/medical-reports/${request_id}`, { headers });
      toast.success("تم حذف الطلب");
      setDetail(null);
      load();
    } catch {
      toast.error("فشل في حذف الطلب");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("ar-SA", { dateStyle: "short", timeStyle: "short" });
    } catch { return iso; }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6" dir="rtl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#4a3fa8] flex items-center gap-2">
            <FileText className="w-7 h-7" />
            طلبات التقارير الطبية
          </h1>
          <p className="text-slate-500 text-sm mt-1">الطلبات الواردة من العيادات عبر رمز QR</p>
        </div>
        <Button onClick={load} variant="outline" data-testid="refresh-mr-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "تحديث"}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-r-4 border-r-amber-400 shadow-sm">
          <CardContent className="pt-5">
            <p className="text-slate-500 text-sm">قيد الانتظار</p>
            <p className="text-3xl font-bold text-amber-600">{stats.pending}</p>
          </CardContent>
        </Card>
        <Card className="border-r-4 border-r-blue-400 shadow-sm">
          <CardContent className="pt-5">
            <p className="text-slate-500 text-sm">قيد التجهيز</p>
            <p className="text-3xl font-bold text-blue-600">{stats.in_progress}</p>
          </CardContent>
        </Card>
        <Card className="border-r-4 border-r-emerald-400 shadow-sm">
          <CardContent className="pt-5">
            <p className="text-slate-500 text-sm">مكتمل</p>
            <p className="text-3xl font-bold text-emerald-600">{stats.completed}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="ابحث برقم الطلب، المريض، الملف، الطبيب..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pr-10 text-right"
            data-testid="mr-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="max-w-xs" data-testid="mr-status-filter"><SelectValue placeholder="جميع الحالات" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">جميع الحالات</SelectItem>
            <SelectItem value="pending">قيد الانتظار</SelectItem>
            <SelectItem value="in_progress">قيد التجهيز</SelectItem>
            <SelectItem value="completed">مكتمل</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-right">رقم الطلب</TableHead>
                <TableHead className="text-right">المريض</TableHead>
                <TableHead className="text-right">رقم الملف</TableHead>
                <TableHead className="text-right">الطبيب المعالج</TableHead>
                <TableHead className="text-right">العيادة</TableHead>
                <TableHead className="text-right">مقدّم الطلب</TableHead>
                <TableHead className="text-right">الحالة</TableHead>
                <TableHead className="text-right">التاريخ</TableHead>
                <TableHead className="text-right">إجراء</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-12">
                  <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p className="text-slate-500">لا توجد طلبات</p>
                </TableCell></TableRow>
              ) : (
                filtered.map((r) => (
                  <TableRow key={r.id} className="hover:bg-slate-50" data-testid={`mr-row-${r.id}`}>
                    <TableCell className="font-mono font-semibold text-[#4a3fa8]">{r.request_number}</TableCell>
                    <TableCell className="font-medium">{r.patient_full_name}</TableCell>
                    <TableCell className="font-mono text-xs">{r.medical_file_number}</TableCell>
                    <TableCell>{r.treating_doctor_name || "—"}</TableCell>
                    <TableCell className="text-slate-600 text-sm">{r.clinic_name}</TableCell>
                    <TableCell className="text-slate-600 text-sm">
                      <div>{r.requester_name}</div>
                      <div className="text-xs text-slate-400 font-mono" dir="ltr">{r.requester_whatsapp}</div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${STATUS_LABELS[r.status]?.color} border`} variant="outline">
                        {STATUS_LABELS[r.status]?.ar || r.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{formatDate(r.created_at)}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline" onClick={() => openDetail(r)} data-testid={`mr-open-${r.id}`}>
                        فتح
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#4a3fa8]">
              <FileText className="w-5 h-5" />
              تفاصيل الطلب — <span className="font-mono">{detail?.request_number}</span>
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="bg-slate-50 p-3 rounded-md">
                  <p className="text-xs text-slate-500 mb-1">المريض</p>
                  <p className="font-medium">{detail.patient_full_name}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-md">
                  <p className="text-xs text-slate-500 mb-1">رقم الملف الطبي</p>
                  <p className="font-mono">{detail.medical_file_number}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-md">
                  <p className="text-xs text-slate-500 mb-1">الطبيب المعالج</p>
                  <p>{detail.treating_doctor_name} {detail.treating_doctor_specialty && (<span className="text-slate-400 text-xs">({detail.treating_doctor_specialty})</span>)}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-md">
                  <p className="text-xs text-slate-500 mb-1">العيادة</p>
                  <p>{detail.clinic_name}</p>
                </div>
                <div className="bg-sky-50 p-3 rounded-md sm:col-span-2">
                  <p className="text-xs text-slate-500 mb-1">مقدّم الطلب</p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="flex items-center gap-1 font-medium"><User className="w-4 h-4" /> {detail.requester_name}</span>
                    <a
                      href={`https://wa.me/${(detail.requester_whatsapp || "").replace(/\D/g, "")}`}
                      target="_blank" rel="noreferrer"
                      className="flex items-center gap-1 text-green-600 hover:underline font-mono" dir="ltr"
                    >
                      <Phone className="w-4 h-4" /> {detail.requester_whatsapp}
                    </a>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
                <div className="flex items-center gap-1"><Clock className="w-3 h-3" /> استلم: {formatDate(detail.created_at)}</div>
                {detail.started_at && <div className="flex items-center gap-1"><Clock className="w-3 h-3" /> بدأ: {formatDate(detail.started_at)}</div>}
                {detail.completed_at && <div className="flex items-center gap-1"><Clock className="w-3 h-3" /> اكتمل: {formatDate(detail.completed_at)}</div>}
              </div>

              <div>
                <p className="text-sm text-slate-600 mb-1">ملاحظات داخلية</p>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="ملاحظات..."
                  className="text-right min-h-[80px]"
                  data-testid="mr-notes-textarea"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 flex-wrap">
            {isAdmin && detail && (
              <Button
                variant="outline"
                className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
                onClick={() => deleteRequest(detail.id)}
                disabled={saving}
                data-testid="mr-delete-btn"
              >
                <Trash2 className="w-4 h-4 ml-1" /> حذف الطلب
              </Button>
            )}
            {detail?.status === "pending" && (
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => changeStatus(detail.id, "in_progress", notes)}
                disabled={saving}
                data-testid="mr-mark-in-progress"
              >
                <PlayCircle className="w-4 h-4 ml-1" /> بدء التجهيز
              </Button>
            )}
            {detail?.status !== "completed" && (
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => changeStatus(detail.id, "completed", notes)}
                disabled={saving}
                data-testid="mr-mark-completed"
              >
                <CheckCircle2 className="w-4 h-4 ml-1" /> تحديد كمكتمل + إشعار مقدّم الطلب
              </Button>
            )}
            <Button variant="outline" onClick={() => setDetail(null)}>إغلاق</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MedicalReportRequests;
